import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  HiOutlineUserGroup,
  HiOutlineDatabase,
  HiOutlineChartBar,
  HiOutlineCog,
  HiOutlineChatAlt2,
  HiOutlineDeviceMobile,
  HiOutlineMail,
  HiOutlineShieldCheck,
  HiOutlinePuzzle,
  HiOutlineCheck,
  HiOutlineArrowRight,
  HiOutlineX,
} from 'react-icons/hi';

// Sign-up destinations — keep these distinct so Free and Pro CTAs never cross.
const TRIAL_URL = 'https://trial.crm.online.ge';        // Pro: 3-month Pro trial
const FREE_URL  = 'https://free.crm.online.ge/register'; // Free: founding-member free plan

// Screenshot shown inside each feature modal. Only keys listed here render an image;
// features without a real screenshot yet simply omit the panel.
// A value can be a single filename or an array (multiple shots shown side by side).
const FEATURE_SHOTS = {
  leadManagement: 'leadManagement.png',
  clientDatabase: 'clientDatabase.jpg',
  analytics: 'analytics.jpg',
  taskAutomation: 'taskAutomation.jpg',
  teamCollaboration: 'teamCollaboration.jpg',
  mobileReady: ['mobileReady.PNG', 'mobileReady1.PNG'],
  communication: 'communication.png',
  integrations: 'sales.png',
  security: 'security.png',
};

const features = [
  { key: 'leadManagement', icon: HiOutlineUserGroup, color: 'purple', span: false },
  { key: 'clientDatabase', icon: HiOutlineDatabase, color: 'blue', span: true },
  { key: 'analytics', icon: HiOutlineChartBar, color: 'green', span: false },
  { key: 'taskAutomation', icon: HiOutlineCog, color: 'amber', span: false },
  { key: 'teamCollaboration', icon: HiOutlineChatAlt2, color: 'cyan', span: false },
  { key: 'mobileReady', icon: HiOutlineDeviceMobile, color: 'rose', span: true },
  { key: 'communication', icon: HiOutlineMail, color: 'violet', span: false },
  { key: 'security', icon: HiOutlineShieldCheck, color: 'slate', span: false },
  { key: 'integrations', icon: HiOutlinePuzzle, color: 'orange', span: false },
];


export default function Landing() {
  const { t, i18n } = useTranslation();
  const [yearly, setYearly] = useState(false);
  const [openFeature, setOpenFeature] = useState(null);
  const [contact, setContact] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactStatus, setContactStatus] = useState('idle'); // idle | sending | sent | error

  const submitContact = async (e) => {
    e.preventDefault();
    if (contactStatus === 'sending') return;
    if (!contact.name.trim() || (!contact.email.trim() && !contact.phone.trim())) {
      setContactStatus('error');
      return;
    }
    setContactStatus('sending');
    try {
      await addDoc(collection(db, 'contactMessages'), {
        name: contact.name.trim().slice(0, 120),
        email: contact.email.trim().slice(0, 200),
        phone: contact.phone.trim().slice(0, 60),
        message: contact.message.trim().slice(0, 4000),
        lang: i18n.language || '',
        createdAt: serverTimestamp(),
      });
      setContact({ name: '', email: '', phone: '', message: '' });
      setContactStatus('sent');
    } catch (err) {
      console.error('Contact submit failed:', err);
      setContactStatus('error');
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.06 }
    );
    document.querySelectorAll('.section').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (openFeature) {
      document.body.style.overflow = 'hidden';
      const onEsc = (e) => { if (e.key === 'Escape') setOpenFeature(null); };
      window.addEventListener('keydown', onEsc);
      return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onEsc); };
    }
  }, [openFeature]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const activeFeature = features.find(f => f.key === openFeature);
  const activeShot = openFeature ? FEATURE_SHOTS[openFeature] : null;

  return (
    <>
      {/* ========== HERO — DARK ========== */}
      <section className="hero">
        <div className="hero-grid-lines" />

        <div className="hero-inner">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            {t('hero.badge')}
          </div>
          <h1>
            {t('hero.title')}<br />
            <span>{t('hero.titleHighlight')}</span>
          </h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>
          <div className="hero-buttons">
            <a href="https://free.crm.online.ge/register" className="btn btn-accent btn-lg">
              {t('hero.cta')}
              <HiOutlineArrowRight />
            </a>
            <a
              href="#features"
              className="btn btn-secondary btn-lg"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </div>
      </section>

      {/* ========== FEATURES — BENTO ========== */}
      <section id="features" className="section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('features.sectionTag')}</div>
            <h2>{t('features.title')} <span>{t('features.titleHighlight')}</span></h2>
            <p>{t('features.subtitle')}</p>
          </div>
          <div className="bento-grid">
            {features.map(({ key, icon: Icon, color, span }) => (
              <div className={`bento-card${span ? ' span-2' : ''}`} key={key} onClick={() => setOpenFeature(key)}>
                <div className={`bento-icon ${color}`}>
                  <Icon size={24} />
                </div>
                <h3>{t(`features.${key}.title`)}</h3>
                <p>{t(`features.${key}.desc`)}</p>
                <span className="bento-expand-hint">{t('features.clickToExpand')}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS — DARK ========== */}
      <section className="section dark-section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('howItWorks.sectionTag')}</div>
            <h2>{t('howItWorks.title')} <span>{t('howItWorks.titleHighlight')}</span></h2>
          </div>
          <div className="steps-row">
            {['step1', 'step2', 'step3'].map((step, i) => (
              <div className="step-item" key={step}>
                <div className="step-num">{i + 1}</div>
                <h3>{t(`howItWorks.${step}.title`)}</h3>
                <p>{t(`howItWorks.${step}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section id="customers" className="section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('testimonials.sectionTag')}</div>
            <h2>{t('testimonials.title')} <span>{t('testimonials.titleHighlight')}</span></h2>
            <p>{t('testimonials.subtitle')}</p>
          </div>
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="testimonial-quote-mark">“</div>
              <p className="testimonial-text">{t('testimonials.rene.quote')}</p>
              <div className="testimonial-author">
                <a href="https://renegroup.ge" target="_blank" rel="noopener noreferrer" aria-label="Rene Development">
                  <img className="testimonial-logo" src="/rene-logo.png" alt="Rene Development" loading="lazy" />
                </a>
                <div className="testimonial-author-meta">
                  <span className="testimonial-name">{t('testimonials.rene.name')}</span>
                  <span className="testimonial-role">{t('testimonials.rene.role')}</span>
                </div>
              </div>
            </div>
          </div>
          <p className="testimonial-trustline">{t('testimonials.trustline')}</p>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section id="pricing" className="section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('pricing.sectionTag')}</div>
            <h2>{t('pricing.title')} <span>{t('pricing.titleHighlight')}</span></h2>
            <p>{t('pricing.subtitle')}</p>
          </div>
          <div className="plan-diff">
            <p className="plan-diff-title">{t('pricing.difference.title')}</p>
            <div className="plan-diff-cols">
              <div className="plan-diff-col">
                <span className="plan-diff-badge free">{t('pricing.difference.freeLabel')}</span>
                <p>{t('pricing.difference.freeText')}</p>
              </div>
              <div className="plan-diff-col">
                <span className="plan-diff-badge trial">{t('pricing.difference.trialLabel')}</span>
                <p>{t('pricing.difference.trialText')}</p>
              </div>
            </div>
          </div>
          <div className="pricing-toggle">
            <span className={!yearly ? 'active' : ''}>{t('pricing.monthly')}</span>
            <button
              className={`toggle-switch ${yearly ? 'active' : ''}`}
              onClick={() => setYearly(!yearly)}
              aria-label="Toggle yearly"
            />
            <span className={yearly ? 'active' : ''}>{t('pricing.yearly')}</span>
            {yearly && <span className="yearly-badge">{t('pricing.yearlyDiscount')}</span>}
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>{t('pricing.starter.name')}</h3>
              <div className="pricing-price">
                <span className="amount">{t('pricing.starter.price')}</span>
                <span className="period">{t('pricing.starter.period')}</span>
              </div>
              <p className="pricing-desc">{t('pricing.starter.desc')}</p>
              <ul className="pricing-features">
                {['f1','f2','f3','f4','f5'].map(f => (
                  <li key={f}><HiOutlineCheck className="check" /> {t(`pricing.starter.${f}`)}</li>
                ))}
              </ul>
              <a href={FREE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary-light">{t('pricing.starter.cta')}</a>
            </div>

            <div className="pricing-card popular">
              <div className="pricing-popular-badge">{t('pricing.professional.popular')}</div>
              <h3>{t('pricing.professional.name')}</h3>
              <div className="pricing-price">
                <span className="amount">{yearly ? '₾239' : t('pricing.professional.price')}</span>
                <span className="period">{t('pricing.professional.period')}</span>
              </div>
              <p className="pricing-desc">{t('pricing.professional.desc')}</p>
              <ul className="pricing-features">
                {['f1','f2','f3','f4','f5','f6','f7'].map(f => (
                  <li key={f}><HiOutlineCheck className="check" /> {t(`pricing.professional.${f}`)}</li>
                ))}
              </ul>
              <a href={TRIAL_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">{t('pricing.professional.cta')}</a>
            </div>

            <div className="pricing-card">
              <h3>{t('pricing.enterprise.name')}</h3>
              <div className="pricing-price">
                <span className="amount">{t('pricing.enterprise.price')}</span>
              </div>
              <p className="pricing-desc">{t('pricing.enterprise.desc')}</p>
              <ul className="pricing-features">
                {['f1','f2','f3','f4','f5','f6'].map(f => (
                  <li key={f}><HiOutlineCheck className="check" /> {t(`pricing.enterprise.${f}`)}</li>
                ))}
              </ul>
              <a href="#about" className="btn btn-secondary-light">{t('pricing.enterprise.cta')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ABOUT ========== */}
      <section id="about" className="section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('about.sectionTag')}</div>
            <h2>{t('about.title')} <span>{t('about.titleHighlight')}</span></h2>
          </div>
          <div className="about-content">
            <div className="about-text">
              <p>{t('about.p1')}</p>
              <p>{t('about.p2')}</p>
              <p>{t('about.p3')}</p>
            </div>
            <div className="about-cards">
              <div className="about-card">
                <h3>{t('about.mission.title')}</h3>
                <p>{t('about.mission.text')}</p>
              </div>
              <div className="about-card">
                <h3>{t('about.vision.title')}</h3>
                <p>{t('about.vision.text')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA — DARK BOX ========== */}
      <section className="cta-section">
        <div className="cta-box">
          <div className="cta-inner">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.subtitle')}</p>
            <a href="https://free.crm.online.ge/register" className="btn btn-accent btn-lg">
              {t('cta.button')}
              <HiOutlineArrowRight />
            </a>
            <p className="cta-note">{t('cta.note')}</p>
          </div>
        </div>
      </section>

      {/* ========== CONTACT ========== */}
      <section id="contact" className="section">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">{t('contact.sectionTag')}</div>
            <h2>{t('contact.title')} <span>{t('contact.titleHighlight')}</span></h2>
            <p>{t('contact.subtitle')}</p>
          </div>

          <form className="contact-form" onSubmit={submitContact}>
            {contactStatus === 'sent' ? (
              <div className="contact-success">
                <div className="contact-success-icon"><HiOutlineCheck size={30} /></div>
                <h3>{t('contact.successTitle')}</h3>
                <p>{t('contact.successText')}</p>
                <button type="button" className="btn btn-secondary-light" onClick={() => setContactStatus('idle')}>
                  {t('contact.sendAnother')}
                </button>
              </div>
            ) : (
              <>
                <div className="contact-row">
                  <div className="contact-field">
                    <label>{t('contact.name')} *</label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      placeholder={t('contact.namePlaceholder')}
                      maxLength={120}
                      required
                    />
                  </div>
                  <div className="contact-field">
                    <label>{t('contact.phone')}</label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                      placeholder={t('contact.phonePlaceholder')}
                      maxLength={60}
                    />
                  </div>
                </div>
                <div className="contact-field">
                  <label>{t('contact.email')}</label>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder={t('contact.emailPlaceholder')}
                    maxLength={200}
                  />
                </div>
                <div className="contact-field">
                  <label>{t('contact.message')}</label>
                  <textarea
                    rows={4}
                    value={contact.message}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })}
                    placeholder={t('contact.messagePlaceholder')}
                    maxLength={4000}
                  />
                </div>
                {contactStatus === 'error' && (
                  <p className="contact-error">{t('contact.error')}</p>
                )}
                <button type="submit" className="btn btn-primary btn-lg" disabled={contactStatus === 'sending'}>
                  {contactStatus === 'sending' ? t('contact.sending') : t('contact.submit')}
                  {contactStatus !== 'sending' && <HiOutlineArrowRight />}
                </button>
                <p className="contact-hint">{t('contact.hint')}</p>
              </>
            )}
          </form>
        </div>
      </section>

      {/* ========== FEATURE MODAL ========== */}
      {openFeature && activeFeature && (
        <div className="feature-modal-overlay" onClick={() => setOpenFeature(null)}>
          <div className="feature-modal" onClick={e => e.stopPropagation()}>
            <button className="feature-modal-close" onClick={() => setOpenFeature(null)}>
              <HiOutlineX size={22} />
            </button>
            <div className="feature-modal-body">
              <div className="feature-modal-info">
                <div className={`bento-icon ${activeFeature.color}`} style={{ width: 56, height: 56 }}>
                  <activeFeature.icon size={28} />
                </div>
                <h2>{t(`features.${openFeature}.title`)}</h2>
                <p className="feature-modal-desc">{t(`features.${openFeature}.extDesc`)}</p>
                <ul className="feature-modal-highlights">
                  {['h1','h2','h3','h4'].map(h => (
                    <li key={h}><HiOutlineCheck className="check" /> {t(`features.${openFeature}.${h}`)}</li>
                  ))}
                </ul>
              </div>
              {activeShot && (
                <div className={`feature-modal-screenshot${openFeature === 'mobileReady' ? ' portrait' : ''}`}>
                  {(Array.isArray(activeShot) ? activeShot : [activeShot]).map(shot => (
                    <img
                      key={shot}
                      src={`/screenshots/${shot}`}
                      alt={t(`features.${openFeature}.title`)}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
